/**
 * Subscription Purchase Provider Component
 * 
 * Provider component for subscription purchase context.
 * Separated from context definition to satisfy Fast Refresh requirements.
 * 
 * @module subscription/context
 */

import { useState, useCallback, useRef, type ReactNode } from 'react';
import { usePromoCodeCapture, SUBSCRIPTION_PROMO_KEY } from '~/features/promotions';
import type { FormattedSubscriptionPlan } from '../types';
import type { User } from '~/services/auth/types';
import { 
  SubscriptionPurchaseContext,
  type GiftRecipientDraft,
  type SubscriptionPurchaseContextValue,
  type SubscriptionTab,
  type PaymentProvider
} from './SubscriptionPurchaseContext';

/** Module scope — a stable empty list (react-state convention: no fresh `[]` per render). */
const NO_RECIPIENTS: GiftRecipientDraft[] = [];

/**
 * Props for SubscriptionPurchaseProvider
 */
interface SubscriptionPurchaseProviderProps {
  children: ReactNode;
}

/**
 * Subscription Purchase Provider
 * 
 * Manages subscription purchase flow state.
 * 
 * **Important**: This provider should only wrap subscription purchase routes,
 * not the entire app. Mount it in the subscription routes configuration.
 * 
 * **Note**: Unlike BookingContext, we don't persist to sessionStorage for security.
 * Purchase flow is expected to complete in one session.
 * 
 * @example
 * ```tsx
 * // In routes/subscription.routes.tsx
 * {
 *   path: 'subscriptions/purchase',
 *   element: (
 *     <SubscriptionPurchaseProvider>
 *       <PaymentProvider>
 *         <Outlet />
 *       </PaymentProvider>
 *     </SubscriptionPurchaseProvider>
 *   ),
 *   children: [
 *     { index: true, element: <SubscriptionPurchasePage /> },
 *     { path: 'review', element: <SubscriptionReviewStep /> },
 *     // ...
 *   ]
 * }
 * ```
 */
export function SubscriptionPurchaseProvider({ children }: SubscriptionPurchaseProviderProps) {
  // Selection state
  const [selectedSubscription, setSelectedSubscription] = useState<FormattedSubscriptionPlan | null>(null);
  const [selectedTab, setSelectedTab] = useState<SubscriptionTab>('memberships');
  
  // Payment & user state
  const [guestUser, setGuestUserState] = useState<User | null>(null);
  const [paymentMethodId, setPaymentMethodIdState] = useState<number | null>(null);
  const [paymentProvider, setPaymentProviderState] = useState<PaymentProvider>(null);
  const [taxRate, setTaxRate] = useState<number>(0);

  // Promo code (7a) — the ONE claim of `?promo=` for the whole funnel; steps
  // read it from this context so URL-vs-storage precedence resolves once.
  const { promoCode, setPromoCode, clearPromoCode } = usePromoCodeCapture(SUBSCRIPTION_PROMO_KEY);

  // Gifting — an order of N passes. Captured input only (see the context types).
  const [quantity, setQuantityState] = useState<number>(1);
  // `null` = the buyer has not answered. See the context type: it is what lets a
  // membership holder's default be overridden and STAY overridden.
  const [giftAll, setGiftAllState] = useState<boolean | null>(null);
  const [recipients, setRecipients] = useState<GiftRecipientDraft[]>(NO_RECIPIENTS);

  const setQuantity = useCallback((next: number) => {
    setQuantityState(Number.isInteger(next) && next >= 1 ? next : 1);
  }, []);

  const setGiftAll = useCallback((next: boolean) => {
    setGiftAllState(next);
  }, []);

  const setRecipient = useCallback((index: number, patch: Partial<GiftRecipientDraft>) => {
    setRecipients((prev) => {
      const next = prev.slice();
      while (next.length <= index) next.push({ name: '', email: '' });
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }, []);

  // A different plan is a different order — never carry a quantity, or the
  // decision to give it all away, across plans.
  // Compared by identity (type + id), not by reference: re-selecting the SAME
  // plan (a preselect effect re-running, a refetched list) must not reset a
  // quantity the buyer already chose.
  const selectedPlanRef = useRef<FormattedSubscriptionPlan | null>(null);
  const selectSubscription = useCallback((subscription: FormattedSubscriptionPlan | null) => {
    const prev = selectedPlanRef.current;
    const samePlan =
      prev !== null &&
      subscription !== null &&
      prev.subscriptionType === subscription.subscriptionType &&
      String(prev.id) === String(subscription.id);
    if (!samePlan) {
      setQuantityState(1);
      setGiftAllState(null);
    }
    selectedPlanRef.current = subscription;
    setSelectedSubscription(subscription);
  }, []);
  
  /**
   * Set guest user (full User object)
   * 
   * @param user - Full User object from /users/guest or authentication
   * @param isAuthenticated - Whether user is authenticated (optional)
   */
  const setGuestUser = useCallback((user: User | null, isAuthenticated?: boolean) => {
    console.log('[SubscriptionPurchaseContext] Setting guest user:', {
      userId: user?.id,
      email: user?.email,
      authStatus: user?.authStatus,
      isAuthenticated
    });
    setGuestUserState(user);
  }, []);
  
  /**
   * Set payment method ID and provider together
   */
  const setPaymentMethodId = useCallback((id: number | null, provider: PaymentProvider) => {
    setPaymentMethodIdState(id);
    setPaymentProviderState(provider);
  }, []);
  
  /**
   * Clear all purchase state
   * 
   * Call this after:
   * - Successful purchase completion
   * - User cancels the purchase flow
   * - Navigation away from purchase flow
   */
  const clearPurchase = useCallback(() => {
    selectedPlanRef.current = null;
    setSelectedSubscription(null);
    setSelectedTab('memberships');
    setGuestUserState(null);
    setPaymentMethodIdState(null);
    setPaymentProviderState(null);
    setQuantityState(1);
    setGiftAllState(null);
    setRecipients(NO_RECIPIENTS);
    // Note: Don't reset taxRate - it's club-specific, not purchase-specific
  }, []);
  
  // Build context value
  const contextValue: SubscriptionPurchaseContextValue = {
    // State
    selectedSubscription,
    selectedTab,
    guestUser,
    paymentMethodId,
    paymentProvider,
    taxRate,
    promoCode,
    quantity,
    giftAll,
    recipients,
    
    // Actions
    setSelectedSubscription: selectSubscription,
    setSelectedTab,
    setGuestUser,
    setPaymentMethodId,
    setTaxRate,
    clearPurchase,
    setPromoCode,
    clearPromoCode,
    setQuantity,
    setGiftAll,
    setRecipient,
  };
  
  return (
    <SubscriptionPurchaseContext.Provider value={contextValue}>
      {children}
    </SubscriptionPurchaseContext.Provider>
  );
}
